import {
  Alert,
  Button,
  Dropdown,
  Flex,
  Input,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AssetOverviewTable } from '~/app/components/asset-overview/AssetOverviewTable';
import { EvimoListPanel } from '~/app/components/common/EvimoListPanel/EvimoListPanel';
import { FilterBar } from '~/app/components/common/FilterBar/FilterBar';
import {
  FilterBarActions,
  FilterBarAddButton,
  FilterBarRefreshButton,
} from '~/app/components/common/FilterBar/FilterBarToolbar';
import type { AssetOverviewViewProps } from './AssetOverview.types';

const { Paragraph, Text, Title } = Typography;

export function AssetOverviewView({
  assetModelOptions,
  assets,
  assetTypeOptions,
  error,
  hasActiveFilters,
  isFetching,
  isLoading,
  isLoadingAssetModels,
  isLoadingAssetTypes,
  isWideLayout,
  onAddAssetAction,
  onAssetModelChangeAction,
  onAssetTypeChangeAction,
  onClearFiltersAction,
  onRefreshAction,
  onSearchChangeAction,
  onTableChangeAction,
  pagination,
  searchValue,
  selectedAssetModelId,
  selectedAssetTypeId,
}: AssetOverviewViewProps) {
  const { t } = useTranslation('assets');

  return (
    <Space orientation="vertical" size={24} style={{ width: '100%' }}>
      <div>
        <Title level={2} style={{ marginBottom: 8 }}>
          {t('overview.title', 'Assets List')}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t(
            'overview.description',
            'Browse the assets available for the current organization workspace.',
          )}
        </Paragraph>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={t('overview.loadFailed', 'Failed to load assets.')}
          description={error.message}
        />
      ) : null}

      <EvimoListPanel>
        <FilterBar>
          <Input
            allowClear
            className="evimo-filter-search"
            prefix={<SearchOutlined />}
            placeholder={t(
              'overview.searchPlaceholder',
              'Search by asset name, serial number, asset type or asset model',
            )}
            value={searchValue}
            onChange={(event) => onSearchChangeAction(event.target.value)}
          />

          <Dropdown
            trigger={['click']}
            dropdownRender={() => (
              <div
                className="evimo-filter-panel"
                style={{ width: isWideLayout ? 320 : 280 }}
              >
                <Flex vertical gap={12}>
                  <div className="evimo-filter-panel__field">
                    <span className="evimo-filter-panel__label">
                      {t('overview.assetTypeLabel', 'Asset type')}
                    </span>
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder={t(
                        'overview.assetTypePlaceholder',
                        'Select an asset type',
                      )}
                      value={selectedAssetTypeId || undefined}
                      options={assetTypeOptions}
                      loading={isLoadingAssetTypes}
                      onClear={() => onAssetTypeChangeAction('')}
                      onChange={onAssetTypeChangeAction}
                    />
                  </div>

                  <div className="evimo-filter-panel__field">
                    <span className="evimo-filter-panel__label">
                      {t('overview.assetModelLabel', 'Asset model')}
                    </span>
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder={t(
                        'overview.assetModelPlaceholder',
                        'Select an asset model',
                      )}
                      value={selectedAssetModelId || undefined}
                      options={assetModelOptions}
                      loading={isLoadingAssetModels}
                      disabled={
                        !selectedAssetTypeId || assetModelOptions.length === 0
                      }
                      onClear={() => onAssetModelChangeAction('')}
                      onChange={onAssetModelChangeAction}
                    />
                  </div>

                  {!isLoadingAssetModels &&
                  selectedAssetTypeId &&
                  assetModelOptions.length === 0 ? (
                    <Text type="secondary">
                      {t(
                        'overview.noAssetModels',
                        'No asset models are available for the selected asset type.',
                      )}
                    </Text>
                  ) : null}

                  {hasActiveFilters ? (
                    <Flex justify="flex-end">
                      <Button type="text" onClick={onClearFiltersAction}>
                        {t('overview.clearFilters', 'Clear filters')}
                      </Button>
                    </Flex>
                  ) : null}
                </Flex>
              </div>
            )}
          >
            <Button
              type="default"
              icon={<FilterOutlined />}
              aria-label={t('overview.filters', 'Filter assets')}
            />
          </Dropdown>

          <FilterBarActions>
            <FilterBarRefreshButton
              loading={isFetching}
              labelKey="overview.refresh"
              ns="assets"
              onClick={() => {
                onRefreshAction();
              }}
            />
            <FilterBarAddButton
              labelKey="overview.addAsset"
              ns="assets"
              defaultLabel="Add asset"
              onClick={onAddAssetAction}
            />
          </FilterBarActions>
        </FilterBar>

        {hasActiveFilters && (selectedAssetTypeId || selectedAssetModelId) ? (
          <div className="evimo-list-panel__tags">
            {selectedAssetTypeId ? (
              <Tag closable onClose={() => onAssetTypeChangeAction('')}>
                {assetTypeOptions.find(
                  (opt) => opt.value === selectedAssetTypeId,
                )?.label || t('overview.unknownFilter', 'Unknown')}
              </Tag>
            ) : null}
            {selectedAssetModelId ? (
              <Tag closable onClose={() => onAssetModelChangeAction('')}>
                {assetModelOptions.find(
                  (opt) => opt.value === selectedAssetModelId,
                )?.label || t('overview.unknownFilter', 'Unknown')}
              </Tag>
            ) : null}
          </div>
        ) : null}

        <AssetOverviewTable
          assets={assets}
          isLoading={isLoading}
          isFetching={isFetching}
          pagination={pagination}
          onTableChange={onTableChangeAction}
        />
      </EvimoListPanel>
    </Space>
  );
}
